import { faPen } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Select, { type SelectChangeEvent } from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import { useCallback, useState } from 'react';

import useStyles from './InlineEditSelect.styles';
import type { InlineEditSelectProps } from './InlineEditSelect.types';

const InlineEditSelect = ({
  value,
  options,
  onSave,
  disabled = false,
  emptyDisplay = '--',
  placeholder = 'Select value'
}: InlineEditSelectProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const { classes } = useStyles({ disabled });

  const handleStartEdit = useCallback(() => {
    if (!disabled) {
      setIsEditing(true);
    }
  }, [disabled]);

  const handleStopEdit = useCallback(() => {
    setIsEditing(false);
  }, []);

  const handleChange = useCallback(
    (event: SelectChangeEvent) => {
      const newValue = event.target.value;
      setIsEditing(false);

      const selectedOption = options.find(
        (option) => String(option.value) === newValue
      );

      if (selectedOption?.disabled) {
        return;
      }

      if (newValue !== value) {
        onSave(newValue);
      }
    },
    [onSave, options, value]
  );

  if (isEditing) {
    return (
      <FormControl className={classes.selectContainer} fullWidth size="small">
        <Select
          autoFocus
          open
          displayEmpty
          value={value || ''}
          onChange={handleChange}
          onClose={handleStopEdit}
        >
          {placeholder && (
            <MenuItem value="" disabled>
              {placeholder}
            </MenuItem>
          )}
          {options.map((option) => (
            <MenuItem
              key={option.value}
              value={String(option.value)}
              disabled={option.disabled}
            >
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }

  const selectedLabel = options.find(
    (option) => String(option.value) === value
  )?.label;

  return (
    <Box className={classes.displayContainer} onClick={handleStartEdit}>
      <Typography className={classes.valueText} variant="body1">
        {selectedLabel || value || emptyDisplay}
      </Typography>
      {!disabled && (
        <IconButton
          className={`${classes.editIcon} inline-edit-icon`}
          onClick={handleStartEdit}
          size="small"
          sx={{ p: 0.5, pr: 1 }}
        >
          <FontAwesomeIcon icon={faPen} size="xs" />
        </IconButton>
      )}
    </Box>
  );
};

InlineEditSelect.displayName = 'InlineEditSelect';

export default InlineEditSelect;
